import {
  clearJourneySession,
  getJourneySessionId,
  initJourneySession,
} from "@zenith/utils/journeySession";

/**
 * Paygilant Web SDK wiring (spec §2.2, §6.1, §9.1). Monitor-only: nothing here
 * affects what the app does, and every failure leaves the app running without
 * the SDK.
 */

declare global {
  interface Window {
    /** Polled by the SDK script so it is keyed to our journey, not a session it mints. */
    getExtSessionId?: () => string | undefined;
    /** The same hook under the name Paygilant's docs use. */
    getSessionId?: () => string | undefined;
    /** Defined by the snippet in index.html; injects the SDK script once. */
    loadPaygilantSdk?: () => void;
  }
}

// Paygilant gives us the exact name for each web URL the SDK runs on.
const LOGOUT_COOKIE = import.meta.env.VITE_PAYGILANT_LOGOUT_COOKIE;
const LOGOUT_COOKIE_MAX_AGE_SECONDS = 300;

function loadSdkOnceKeyed(id: string | undefined) {
  // The SDK reads our id once per page load, so it is only injected once there
  // is an id to give it - otherwise it would mint its own session and its
  // telemetry would never join our checkpoint calls (spec §2.5).
  if (id) window.loadPaygilantSdk?.();
}

export function startPaygilant() {
  window.getExtSessionId = getJourneySessionId;
  window.getSessionId = getJourneySessionId;
  void initJourneySession().then(loadSdkOnceKeyed);
}

function signalPaygilantLogout() {
  if (!LOGOUT_COOKIE) return;
  // Set here rather than by the API: the API is on another domain, so a
  // Set-Cookie from it is never visible to the SDK on this one (spec §6.1).
  // The attributes match the SDK's own cookies, so its removal of this one on
  // the next load applies to the same cookie.
  const attributes =
    window.location.protocol === "https:"
      ? "; SameSite=None; Secure; Partitioned"
      : "";
  document.cookie = `${LOGOUT_COOKIE}=1; Path=/; Max-Age=${LOGOUT_COOKIE_MAX_AGE_SECONDS}${attributes}`;
}

/**
 * Ends the Paygilant session on logout or session timeout. The SDK only checks
 * for the logout cookie when it loads, so the caller must follow this with a
 * full page load; that load also starts a new journey.
 */
export function endPaygilantSession() {
  clearJourneySession();
  signalPaygilantLogout();
}
